import type {
  ApiResponse,
  PaginatedResponse,
  AuthTokens,
  LoginRequest,
  UserPublic,
  FlowCategory,
  FlowTemplate,
  FlowTemplateDetail,
  Project,
  ProjectDetail,
  ProjectStep,
  ProjectStepDetail,
  Notification,
  UnreadCount,
  ApprovalRecord,
  SystemSettings,
  FlowImprovementRequest,
  InputTemplate,
  GamificationStats,
  DraftData,
  WbsTree,
  WbsTask,
  WbsTaskDetail,
  WbsTemplate,
  WbsTemplateDetail,
  WbsTemplateTask,
  ActivityItem,
  TimelineEvent,
  MonthlyBreakdown,
  Comment,
  WbsLabel,
  WbsFilterParams,
  MyAction,
} from 'shared/types/index.js';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

class ApiClient {
  // NOTE: Tokens in localStorage are vulnerable to XSS. This is an accepted trade-off for SPA.
  // Mitigation: CSP headers, X-Content-Type-Options, no inline scripts.
  // Future improvement: migrate to httpOnly cookies with SameSite=Strict.
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
  }

  setTokens(access: string, refresh: string) {
    this.accessToken = access;
    this.refreshToken = refresh;
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: { formData?: boolean }
  ): Promise<T> {
    const headers: Record<string, string> = {};

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    if (body && !options?.formData) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: options?.formData ? (body as FormData) : body ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
        const retryResponse = await fetch(`${API_BASE}${path}`, {
          method,
          headers,
          body: options?.formData ? (body as FormData) : body ? JSON.stringify(body) : undefined,
        });
        return retryResponse.json();
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      let msg: string;
      if (typeof error.error === 'string') {
        msg = error.error;
      } else if (error.error?.issues) {
        msg = error.error.issues.map((i: { path: string[]; message: string }) => `${i.path.join('.')}: ${i.message}`).join(', ');
      } else {
        msg = error.message || `HTTP ${response.status}`;
      }
      throw new Error(msg);
    }

    // CSV export returns text
    const contentType = response.headers.get('Content-Type');
    if (contentType?.includes('text/csv')) {
      return response.text() as unknown as T;
    }

    return response.json();
  }

  private async tryRefresh(): Promise<boolean> {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });
      if (res.ok) {
        const data: ApiResponse<AuthTokens> = await res.json();
        if (data.success && data.data) {
          this.setTokens(data.data.access_token, data.data.refresh_token);
          return true;
        }
      }
    } catch {
      // refresh failed
    }
    this.clearTokens();
    window.location.href = '/login';
    return false;
  }

  // Auth
  async login(data: LoginRequest) {
    const result = await this.request<ApiResponse<AuthTokens & { user: UserPublic }>>('POST', '/auth/login', data);
    if (result.success && result.data) {
      this.setTokens(result.data.access_token, result.data.refresh_token);
    }
    return result;
  }

  async logout() {
    await this.request<ApiResponse<void>>('POST', '/auth/logout');
    this.clearTokens();
  }

  async getMe() {
    return this.request<ApiResponse<UserPublic>>('GET', '/auth/me');
  }

  // Registration
  async register(data: { login_id: string; password: string; name: string; email: string }) {
    const result = await this.request<ApiResponse<AuthTokens & { user: UserPublic }>>('POST', '/auth/register', data);
    if (result.success && result.data) {
      this.setTokens(result.data.access_token, result.data.refresh_token);
    }
    return result;
  }

  // Password Reset
  async requestPasswordReset(email: string) {
    return this.request<ApiResponse<{ token?: string } | undefined>>('POST', '/auth/password-reset/request', { email });
  }

  async executePasswordReset(token: string, newPassword: string) {
    return this.request<ApiResponse<void>>('POST', '/auth/password-reset/execute', { token, new_password: newPassword });
  }

  getGoogleAuthUrl() {
    return `${API_BASE}/auth/google`;
  }

  // Users
  async getUsers() {
    return this.request<ApiResponse<UserPublic[]>>('GET', '/users');
  }

  async createUser(data: Record<string, unknown>) {
    return this.request<ApiResponse<{ id: string }>>('POST', '/users', data);
  }

  async updateUser(id: string, data: Record<string, unknown>) {
    return this.request<ApiResponse<void>>('PUT', `/users/${id}`, data);
  }

  async deleteUser(id: string) {
    return this.request<ApiResponse<void>>('DELETE', `/users/${id}`);
  }

  async updateUserProfile(id: string, data: { name?: string; email?: string; department?: string }) {
    return this.request<ApiResponse<void>>('PUT', `/users/${id}/profile`, data);
  }

  async updateUserSettings(id: string, data: Record<string, unknown>) {
    return this.request<ApiResponse<void>>('PUT', `/users/${id}/settings`, data);
  }

  async setDelegate(id: string, data: { delegate_to: string | null; delegate_until: string | null }) {
    return this.request<ApiResponse<void>>('PUT', `/users/${id}/delegate`, data);
  }

  // Flows
  async getFlows() {
    return this.request<ApiResponse<{ categories: FlowCategory[]; templates: FlowTemplate[] }>>('GET', '/flows');
  }

  async getFlow(id: string) {
    return this.request<ApiResponse<FlowTemplateDetail>>('GET', `/flows/${id}`);
  }

  async createFlow(data: Record<string, unknown>) {
    return this.request<ApiResponse<{ id: string }>>('POST', '/flows', data);
  }

  async updateFlow(id: string, data: Record<string, unknown>) {
    return this.request<ApiResponse<void>>('PUT', `/flows/${id}`, data);
  }

  async saveFlowSteps(flowId: string, steps: Array<Record<string, unknown>>) {
    return this.request<ApiResponse<void>>('PUT', `/flows/${flowId}/steps`, { steps });
  }

  async updateApprovalRoute(routeId: string, data: Record<string, unknown>) {
    return this.request<ApiResponse<void>>('PUT', `/flows/routes/${routeId}`, data);
  }

  async deleteFlow(id: string) {
    return this.request<ApiResponse<void>>('DELETE', `/flows/${id}`);
  }

  async duplicateFlow(id: string) {
    return this.request<ApiResponse<{ id: string }>>('POST', `/flows/${id}/duplicate`);
  }

  async getFlowImprovements(flowId: string) {
    return this.request<ApiResponse<FlowImprovementRequest[]>>('GET', `/flows/${flowId}/improvements`);
  }

  async createFlowImprovement(flowId: string, data: Record<string, unknown>) {
    return this.request<ApiResponse<{ id: string }>>('POST', `/flows/${flowId}/improvements`, data);
  }

  async updateFlowImprovement(id: string, data: Record<string, unknown>) {
    return this.request<ApiResponse<void>>('PUT', `/flows/improvements/${id}`, data);
  }

  async createApprovalRoute(flowId: string, data: Record<string, unknown>) {
    return this.request<ApiResponse<{ id: string }>>('POST', `/flows/${flowId}/routes`, data);
  }

  async deleteApprovalRoute(routeId: string) {
    return this.request<ApiResponse<void>>('DELETE', `/flows/routes/${routeId}`);
  }

  // Projects
  async getProjects(params?: Record<string, string>) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<PaginatedResponse<Project & { overall_progress: number }>>('GET', `/projects${query}`);
  }

  async getProject(id: string) {
    return this.request<ApiResponse<ProjectDetail>>('GET', `/projects/${id}`);
  }

  async createProject(data: Record<string, unknown>) {
    return this.request<ApiResponse<{ id: string }>>('POST', '/projects', data);
  }

  async updateProject(id: string, data: Record<string, unknown>) {
    return this.request<ApiResponse<void>>('PUT', `/projects/${id}`, data);
  }

  async deleteProject(id: string) {
    return this.request<ApiResponse<void>>('DELETE', `/projects/${id}`);
  }

  async copyProject(id: string, data: { title: string; due_date?: string }) {
    return this.request<ApiResponse<{ id: string }>>('POST', `/projects/${id}/copy`, data);
  }

  async getProjectSteps(projectId: string) {
    return this.request<ApiResponse<ProjectStep[]>>('GET', `/projects/${projectId}/steps`);
  }

  async getProjectStep(projectId: string, stepId: string) {
    return this.request<ApiResponse<ProjectStepDetail>>('GET', `/projects/${projectId}/steps/${stepId}`);
  }

  async startStep(projectId: string, stepId: string) {
    return this.request<ApiResponse<void> & { warning?: string }>('PUT', `/projects/${projectId}/steps/${stepId}/start`);
  }

  async updateStepActions(projectId: string, stepId: string, actions: Array<{ action_def_id: string; value?: string; completed: boolean }>) {
    return this.request<ApiResponse<void>>('PUT', `/projects/${projectId}/steps/${stepId}/actions`, { actions });
  }

  async submitStep(projectId: string, stepId: string) {
    return this.request<ApiResponse<{ new_status: string }>>('PUT', `/projects/${projectId}/steps/${stepId}/submit`);
  }

  async requestAiGuidance(projectId: string, stepId: string) {
    return this.request<ApiResponse<void>>('POST', `/projects/${projectId}/steps/${stepId}/ai-guidance`);
  }

  // Approvals
  async getPendingApprovals() {
    return this.request<ApiResponse<(ApprovalRecord & { step_name?: string; project_title?: string; project_id?: string; project_priority?: string })[]>>('GET', '/approvals/pending');
  }

  async getMyActions() {
    return this.request<ApiResponse<MyAction[]>>('GET', '/my-actions');
  }

  async approveStep(projectId: string, stepId: string, comment?: string) {
    return this.request<ApiResponse<{ completed: boolean }>>('POST', `/approvals/${projectId}/steps/${stepId}/approve`, { comment });
  }

  async rejectStep(projectId: string, stepId: string, returnToStepId: string, comment: string) {
    return this.request<ApiResponse<void>>('POST', `/approvals/${projectId}/steps/${stepId}/reject`, { return_to_step_id: returnToStepId, comment });
  }

  async reopenStep(projectId: string, stepId: string, targetStepId: string) {
    return this.request<ApiResponse<void>>('PUT', `/projects/${projectId}/steps/${stepId}/reopen`, { target_step_id: targetStepId });
  }

  // Step flow editing
  async addStep(projectId: string, data: {
    name: string;
    description?: string;
    guidance?: string;
    assignee_id?: string;
    requires_approval?: boolean;
    approval_route_id?: string;
    is_custom?: boolean;
    insert_after_order?: number;
    actions?: Array<{ type: string; label: string; is_required?: boolean }>;
  }) {
    return this.request<ApiResponse<{ id: string }>>('POST', `/projects/${projectId}/steps`, data);
  }

  async updateStep(projectId: string, stepId: string, data: {
    name?: string;
    description?: string | null;
    guidance?: string | null;
    assignee_id?: string | null;
    requires_approval?: boolean;
    approval_route_id?: string | null;
  }) {
    return this.request<ApiResponse<void>>('PUT', `/projects/${projectId}/steps/${stepId}`, data);
  }

  async deleteStep(projectId: string, stepId: string) {
    return this.request<ApiResponse<void>>('DELETE', `/projects/${projectId}/steps/${stepId}`);
  }

  async reorderSteps(projectId: string, stepOrders: Array<{ id: string; step_order: number }>) {
    return this.request<ApiResponse<void>>('PUT', `/projects/${projectId}/steps/reorder`, { step_orders: stepOrders });
  }

  async addActionDef(projectId: string, stepId: string, data: { type: string; label: string; is_required?: boolean }) {
    return this.request<ApiResponse<{ id: string }>>('POST', `/projects/${projectId}/steps/${stepId}/action-defs`, data);
  }

  async updateActionDef(projectId: string, stepId: string, defId: string, data: { label?: string; type?: string; is_required?: boolean }) {
    return this.request<ApiResponse<void>>('PUT', `/projects/${projectId}/steps/${stepId}/action-defs/${defId}`, data);
  }

  async deleteActionDef(projectId: string, stepId: string, defId: string) {
    return this.request<ApiResponse<void>>('DELETE', `/projects/${projectId}/steps/${stepId}/action-defs/${defId}`);
  }

  // Notifications
  async getNotifications(params?: Record<string, string>) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<{ success: boolean; data: Notification[]; total: number }>('GET', `/notifications${query}`);
  }

  async getUnreadCount() {
    return this.request<ApiResponse<UnreadCount>>('GET', '/notifications/unread-count');
  }

  async markNotificationRead(id: string) {
    return this.request<ApiResponse<void>>('PUT', `/notifications/${id}/read`);
  }

  async markNotificationUnread(id: string) {
    return this.request<ApiResponse<void>>('PUT', `/notifications/${id}/unread`);
  }

  async markAllNotificationsRead() {
    return this.request<ApiResponse<void>>('PUT', '/notifications/read-all');
  }

  async deleteNotification(id: string) {
    return this.request<ApiResponse<void>>('DELETE', `/notifications/${id}`);
  }

  async bulkNotificationAction(action: 'read' | 'unread' | 'delete', ids: string[]) {
    return this.request<ApiResponse<void>>('POST', '/notifications/bulk', { action, ids });
  }

  // Reports
  async getReportOverview() {
    return this.request<ApiResponse<Record<string, unknown>>>('GET', '/reports/overview');
  }

  async getCompletionRate(from?: string, to?: string) {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return this.request<ApiResponse<{ total: number; completed: number; rate: number }>>('GET', `/reports/completion-rate?${params}`);
  }

  async getStepDuration() {
    return this.request<ApiResponse<Array<{ name: string; avg_hours: number; count: number }>>>('GET', '/reports/step-duration');
  }

  async getUserWorkload() {
    return this.request<ApiResponse<Array<Record<string, unknown>>>>('GET', '/reports/user-workload');
  }

  async getGroupWorkload() {
    return this.request<ApiResponse<{
      by_user: Array<{ name: string; user_group: string; active_tasks: number; completed_tasks: number; total_tasks: number }>;
      by_group: Array<{ user_group: string; active_tasks: number; completed_tasks: number; total_tasks: number; member_count: number }>;
      steps_by_group: Array<{ user_group: string; step_name: string; count: number; completed: number }>;
    }>>('GET', '/reports/group-workload');
  }

  async getRejectionRate() {
    return this.request<ApiResponse<Array<Record<string, unknown>>>>('GET', '/reports/rejection-rate');
  }

  async exportReport() {
    return this.request<string>('GET', '/reports/export');
  }

  reports = {
    getTimeline: (params: Record<string, string> = {}) => {
      const query = new URLSearchParams(params).toString();
      return this.request<{ data: TimelineEvent[]; total: number }>('GET', `/timeline?${query}`);
    },

    getMonthlyStats: (params: Record<string, string> = {}) => {
      const query = new URLSearchParams(params).toString();
      return this.request<MonthlyBreakdown>('GET', `/reports/monthly?${query}`);
    },
  };

  // Files
  async uploadFile(file: File, actionRecordId: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('action_record_id', actionRecordId);
    return this.request<ApiResponse<{ id: string; file_name: string; file_size: number }>>('POST', '/files/upload', formData, { formData: true });
  }

  async getFileDownloadUrl(id: string): Promise<string> {
    return `${API_BASE}/files/${id}/download`;
  }

  // Input Templates
  async getInputTemplates(actionTemplateId?: string) {
    const params = actionTemplateId ? `?action_template_id=${actionTemplateId}` : '';
    return this.request<ApiResponse<InputTemplate[]>>('GET', `/input-templates${params}`);
  }

  async createInputTemplate(data: Record<string, unknown>) {
    return this.request<ApiResponse<{ id: string }>>('POST', '/input-templates', data);
  }

  // Clients
  async getClients() {
    return this.request<ApiResponse<Array<{ id: string; name: string; code?: string; contact_name?: string; contact_email?: string; phone?: string; notes?: string; is_active: number; created_at: string; updated_at: string }>>>('GET', '/clients');
  }

  async getAllClients() {
    return this.request<ApiResponse<Array<{ id: string; name: string; code?: string; contact_name?: string; contact_email?: string; phone?: string; notes?: string; is_active: number; created_at: string; updated_at: string }>>>('GET', '/clients/all');
  }

  async createClient(data: Record<string, unknown>) {
    return this.request<ApiResponse<{ id: string }>>('POST', '/clients', data);
  }

  async updateClient(id: string, data: Record<string, unknown>) {
    return this.request<ApiResponse<void>>('PUT', `/clients/${id}`, data);
  }

  async deleteClient(id: string) {
    return this.request<ApiResponse<void>>('DELETE', `/clients/${id}`);
  }

  // Gamification
  async getGamificationStats() {
    return this.request<ApiResponse<GamificationStats>>('GET', '/gamification/stats');
  }

  // AI Assistant
  async aiChat(message: string, projectId?: string, stepId?: string) {
    return this.request<ApiResponse<{ reply: string }>>('POST', '/ai/chat', {
      message,
      project_id: projectId,
      step_id: stepId,
    });
  }

  async aiProjectSummary(projectId: string) {
    return this.request<ApiResponse<{ summary: string }>>('GET', `/ai/projects/${projectId}/summary`);
  }

  async aiRejectSuggestion(projectId: string, stepId: string) {
    return this.request<ApiResponse<{ suggestion: string }>>('GET', `/ai/projects/${projectId}/steps/${stepId}/reject-suggestion`);
  }

  async aiReportAnalysis(data: {
    projectStats: Array<{ status: string; count: number }>;
    stepDuration: Array<{ name: string; avg_hours: number; count: number }>;
    userWorkload: Array<{ name: string; count: number }>;
    rejectionRate: Array<{ name: string; rejection_count: number; total_count: number }>;
  }) {
    return this.request<ApiResponse<{ analysis: string }>>('POST', '/ai/reports/analyze', data);
  }

  async aiNotificationPrioritize(notifications: Array<{
    id: string;
    title: string;
    message: string;
    level: string;
    created_at: string;
    project_id?: string;
  }>) {
    return this.request<ApiResponse<{ scored: Array<{ id: string; score: number; reason: string }> }>>('POST', '/ai/notifications/prioritize', { notifications });
  }

  // Drafts
  async getDraft(projectId: string, stepId: string) {
    return this.request<ApiResponse<{ draft: { project_step_id: string; draft_data: DraftData; saved_at: string } | null }>>('GET', `/drafts/${projectId}/steps/${stepId}`);
  }

  async saveDraft(projectId: string, stepId: string, draftData: DraftData) {
    return this.request<ApiResponse<{ saved_at: string; status: string }>>('PUT', `/drafts/${projectId}/steps/${stepId}`, { draft_data: draftData });
  }

  async deleteDraft(projectId: string, stepId: string) {
    return this.request<ApiResponse<{ deleted: boolean }>>('DELETE', `/drafts/${projectId}/steps/${stepId}`);
  }

  // Admin Settings
  async getSystemSettings() {
    return this.request<ApiResponse<Record<string, string>>>('GET', '/admin/settings');
  }

  async updateSystemSettings(data: Record<string, string>) {
    return this.request<ApiResponse<void>>('PUT', '/admin/settings', data);
  }

  // WBS
  async getWbsTree(projectId: string, params?: Pick<WbsFilterParams, 'type'>) {
    const qs = params?.type ? `?type=${encodeURIComponent(params.type)}` : '';
    return this.request<ApiResponse<WbsTree>>('GET', `/wbs/${projectId}${qs}`);
  }

  async getWbsTaskDetail(projectId: string, taskId: string) {
    return this.request<ApiResponse<WbsTaskDetail>>('GET', `/wbs/${projectId}/tasks/${taskId}`);
  }

  async createWbsTask(projectId: string, data: {
    title: string;
    description?: string;
    parent_id?: string;
    assignee_id?: string;
    deadline?: string;
    priority?: string;
    sort_order?: number;
  }) {
    return this.request<ApiResponse<WbsTask>>('POST', `/wbs/${projectId}/tasks`, data);
  }

  async updateWbsTask(projectId: string, taskId: string, data: Record<string, unknown>) {
    return this.request<ApiResponse<WbsTask>>('PUT', `/wbs/${projectId}/tasks/${taskId}`, data);
  }

  async deleteWbsTask(projectId: string, taskId: string) {
    return this.request<ApiResponse<void>>('DELETE', `/wbs/${projectId}/tasks/${taskId}`);
  }

  async completeWbsTask(projectId: string, taskId: string) {
    return this.request<ApiResponse<void>>('PUT', `/wbs/${projectId}/tasks/${taskId}/complete`);
  }

  async reorderWbsTasks(projectId: string, tasks: Array<{ id: string; sort_order: number; parent_id?: string | null }>) {
    return this.request<ApiResponse<void>>('PUT', `/wbs/${projectId}/tasks/reorder`, { tasks });
  }

  async linkFlowToWbsTask(projectId: string, taskId: string, flowProjectId: string) {
    return this.request<ApiResponse<{ id: string }>>('POST', `/wbs/${projectId}/tasks/${taskId}/flows`, { flow_project_id: flowProjectId });
  }

  async createFlowFromTemplateForWbsTask(projectId: string, taskId: string, data: {
    flow_template_id: string;
    title: string;
    description?: string;
    priority?: string;
    due_date?: string;
  }) {
    return this.request<ApiResponse<{ flow_project_id: string; link_id: string }>>('POST', `/wbs/${projectId}/tasks/${taskId}/flows/from-template`, data);
  }

  async unlinkFlowFromWbsTask(projectId: string, taskId: string, linkId: string) {
    return this.request<ApiResponse<void>>('DELETE', `/wbs/${projectId}/tasks/${taskId}/flows/${linkId}`);
  }

  async addWbsDependency(projectId: string, taskId: string, predecessorId: string, type?: string) {
    return this.request<ApiResponse<{ id: string }>>('POST', `/wbs/${projectId}/tasks/${taskId}/dependencies`, { predecessor_id: predecessorId, dependency_type: type });
  }

  async removeWbsDependency(projectId: string, taskId: string, depId: string) {
    return this.request<ApiResponse<void>>('DELETE', `/wbs/${projectId}/tasks/${taskId}/dependencies/${depId}`);
  }

  // WBS Templates
  async getWbsTemplates() {
    return this.request<ApiResponse<WbsTemplate[]>>('GET', '/wbs-templates');
  }

  async getWbsTemplate(id: string) {
    return this.request<ApiResponse<WbsTemplateDetail>>('GET', `/wbs-templates/${id}`);
  }

  async createWbsTemplate(data: { name: string; description?: string }) {
    return this.request<ApiResponse<WbsTemplate>>('POST', '/wbs-templates', data);
  }

  async updateWbsTemplate(id: string, data: { name?: string; description?: string | null; is_active?: boolean }) {
    return this.request<ApiResponse<WbsTemplate>>('PUT', `/wbs-templates/${id}`, data);
  }

  async deleteWbsTemplate(id: string) {
    return this.request<ApiResponse<void>>('DELETE', `/wbs-templates/${id}`);
  }

  async createWbsTemplateTask(templateId: string, data: {
    title: string; description?: string; parent_id?: string;
    flow_template_id?: string; default_priority?: string;
    estimated_days?: number; sort_order?: number;
  }) {
    return this.request<ApiResponse<WbsTemplateTask>>('POST', `/wbs-templates/${templateId}/tasks`, data);
  }

  async updateWbsTemplateTask(templateId: string, taskId: string, data: Record<string, unknown>) {
    return this.request<ApiResponse<void>>('PUT', `/wbs-templates/${templateId}/tasks/${taskId}`, data);
  }

  async deleteWbsTemplateTask(templateId: string, taskId: string) {
    return this.request<ApiResponse<void>>('DELETE', `/wbs-templates/${templateId}/tasks/${taskId}`);
  }

  async expandWbsTemplate(templateId: string, data: {
    title: string; description?: string; client_name?: string;
    priority?: string; due_date?: string; start_date?: string;
    default_assignee_id?: string;
    task_assignees?: Record<string, string>;
  }) {
    return this.request<ApiResponse<{ project_id: string }>>('POST', `/wbs-templates/${templateId}/expand`, data);
  }

  async saveWbsAsTemplate(projectId: string, data: { name: string; description?: string }) {
    return this.request<ApiResponse<{ template_id: string }>>('POST', `/wbs/${projectId}/save-as-template`, data);
  }

  // Activities
  async getActivities(params?: { scope?: string; limit?: string; offset?: string }) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<{ success: boolean; data: ActivityItem[]; total: number }>('GET', `/activities${query}`);
  }

  // Comments
  async getComments(entityType: string, entityId: string) {
    return this.request<{ comments: Comment[]; total: number }>('GET', `/comments/${entityType}/${entityId}`);
  }
  async createComment(entityType: string, entityId: string, data: { body: string; parent_id?: string }) {
    return this.request<{ comment: Comment }>('POST', `/comments/${entityType}/${entityId}`, data);
  }
  async updateComment(id: string, body: string) {
    return this.request<{ comment: Comment }>('PUT', `/comments/${id}`, { body });
  }
  async deleteComment(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/comments/${id}`);
  }

  // WBS Labels
  async getWbsLabels(projectId: string) {
    return this.request<ApiResponse<WbsLabel[]>>('GET', `/wbs/${projectId}/labels`);
  }
  async createWbsLabel(projectId: string, data: { name: string; color: string }) {
    return this.request<{ id: string }>('POST', `/wbs/${projectId}/labels`, data);
  }
  async updateWbsLabel(projectId: string, id: string, data: { name?: string; color?: string }) {
    return this.request<{ success: boolean }>('PUT', `/wbs/${projectId}/labels/${id}`, data);
  }
  async deleteWbsLabel(projectId: string, id: string) {
    return this.request<{ success: boolean }>('DELETE', `/wbs/${projectId}/labels/${id}`);
  }
  async addTaskLabel(projectId: string, taskId: string, labelId: string) {
    return this.request<{ success: boolean }>('POST', `/wbs/${projectId}/tasks/${taskId}/labels/${labelId}`);
  }
  async removeTaskLabel(projectId: string, taskId: string, labelId: string) {
    return this.request<{ success: boolean }>('DELETE', `/wbs/${projectId}/tasks/${taskId}/labels/${labelId}`);
  }

  // WBS Watch
  async watchTask(projectId: string, taskId: string) {
    return this.request<{ success: boolean }>('POST', `/wbs/${projectId}/tasks/${taskId}/watch`);
  }
  async unwatchTask(projectId: string, taskId: string) {
    return this.request<{ success: boolean }>('DELETE', `/wbs/${projectId}/tasks/${taskId}/watch`);
  }

  // WBS Kanban
  async getWbsKanban(projectId: string, params?: Record<string, string>) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<{ columns: Record<string, WbsTask[]> }>('GET', `/wbs/${projectId}/kanban${query}`);
  }

  // ============================================================
  // Issues (wbs_tasks ベース)
  // ============================================================

  async getIssues(stepId: string) {
    return this.request<any[]>('GET', `/steps/${stepId}/issues`);
  }

  async createIssue(stepId: string, data: { title: string; description?: string; type: string; priority: string; assignee_id?: string }) {
    return this.request<any>('POST', `/steps/${stepId}/issues`, data);
  }

  async getIssue(id: string) {
    return this.request<any>('GET', `/issues/${id}`);
  }

  async updateIssue(id: string, data: Record<string, any>) {
    return this.request<any>('PATCH', `/issues/${id}`, data);
  }

  async deleteIssue(id: string) {
    return this.request<{ success: boolean }>('DELETE', `/issues/${id}`);
  }

  async getIssueComments(issueId: string) {
    return this.request<any[]>('GET', `/issues/${issueId}/comments`);
  }

  async createIssueComment(issueId: string, data: { body: string }) {
    return this.request<{ success: boolean }>('POST', `/issues/${issueId}/comments`, data);
  }

  async getAllIssues(params?: Record<string, string>) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<{ items: any[]; total: number; page: number; per_page: number }>('GET', `/issues${query}`);
  }

  async getMyIssues(params?: Record<string, string>) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<any[]>('GET', `/my-issues${query}`);
  }

  async getProjectIssues(projectId: string, params?: Record<string, string>) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<any[]>('GET', `/projects/${projectId}/issues${query}`);
  }

  // Admin Import
  async importBacklogCsv(data: {
    projectId?: string;
    newProjectName?: string;
    tasks: Array<{
      key: string;
      title: string;
      description: string;
      status: string;
      assigneeName?: string;
      priority: string;
      typeName: string;
      parentKey?: string;
      deadline?: string;
      startDate?: string;
      categoryNames?: string[];
    }>;
    statusMapping: Record<string, string>;
    typeMapping: Record<string, string>;
  }): Promise<{ imported: number; skipped: number; projectId: string }> {
    return this.request<{ imported: number; skipped: number; projectId: string }>(
      'POST',
      '/admin/import/backlog',
      data
    );
  }
  // ── SkyView: Backlog連携 ──

  async getBacklogConfig() {
    return this.request('GET', '/backlog/config');
  }

  async saveBacklogConfig(config: { space_url: string; api_key: string; sync_interval_hours?: number }) {
    return this.request('POST', '/backlog/config', config);
  }

  async triggerBacklogSync() {
    return this.request('POST', '/backlog/sync');
  }

  async getBacklogSyncLogs() {
    return this.request('GET', '/backlog/sync/logs');
  }

  async getBacklogMappings() {
    return this.request('GET', '/backlog/mappings');
  }

  async saveBacklogMapping(mapping: { backlog_user_id: number; backlog_user_name: string; procbase_user_id: string }) {
    return this.request('POST', '/backlog/mappings', mapping);
  }

  async getManagerDashboard() {
    return this.request('GET', '/backlog/dashboard');
  }

  async getMonthlyReport(month?: string) {
    const q = month ? `?month=${month}` : '';
    return this.request('GET', `/backlog/reports/monthly${q}`);
  }

  async getReportTrends(months?: number) {
    const q = months ? `?months=${months}` : '';
    return this.request('GET', `/backlog/reports/trends${q}`);
  }

  // ── Backlog統合表示 ──

  async getBacklogProjectList(search?: string) {
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request('GET', `/backlog/projects-list${q}`);
  }

  async getBacklogIssues(params: { project_key?: string; status?: string; assignee?: string; search?: string; page?: number; per_page?: number } = {}) {
    const query = new URLSearchParams();
    if (params.project_key) query.set('project_key', params.project_key);
    if (params.status) query.set('status', params.status);
    if (params.assignee) query.set('assignee', params.assignee);
    if (params.search) query.set('search', params.search);
    if (params.page) query.set('page', String(params.page));
    if (params.per_page) query.set('per_page', String(params.per_page));
    const q = query.toString();
    return this.request('GET', `/backlog/issues-list${q ? `?${q}` : ''}`);
  }

  async getBacklogSummary() {
    return this.request('GET', '/backlog/summary');
  }

  async getBacklogSyncTargets() {
    return this.request<any>('GET', '/backlog/sync-targets');
  }

  async updateBacklogSyncTargets(targets: Array<{ backlog_project_id: number; enabled: boolean }>) {
    return this.request<any>('PUT', '/backlog/sync-targets', { targets });
  }

  async startBacklogFullSync() {
    return this.request<any>('POST', '/backlog/sync/full');
  }

  async getBacklogSyncProgress() {
    return this.request<any>('GET', '/backlog/sync/progress');
  }

  async getBacklogAnalyticsBudget(params?: Record<string, string>) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<any>('GET', `/backlog/analytics/budget${query}`);
  }

  async getBacklogAnalyticsMembers(params?: Record<string, string>) {
    const query = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<any>('GET', `/backlog/analytics/members${query}`);
  }

  async getBacklogAnalyticsHealth() {
    return this.request<any>('GET', '/backlog/analytics/health');
  }

  async getBacklogAnalyticsBottlenecks() {
    return this.request<any>('GET', '/backlog/analytics/bottlenecks');
  }

  async getBacklogBurndown(params: Record<string, string>) {
    const query = '?' + new URLSearchParams(params).toString();
    return this.request<any>('GET', `/backlog/analytics/burndown${query}`);
  }

  async getBacklogIssueDetail(issueKey: string) {
    return this.request<{ data: import('shared/types/index.js').BacklogIssueDetail; backlog_url: string | null }>('GET', `/backlog/issues/${encodeURIComponent(issueKey)}`);
  }

  async getBacklogIssueComments(issueKey: string, page = 1) {
    return this.request<any>('GET', `/backlog/issues/${issueKey}/comments?page=${page}`);
  }

  async getBacklogMilestones(projectKey: string) {
    return this.request<any>('GET', `/backlog/milestones?project_key=${projectKey}`);
  }

  async getBacklogCategories(projectKey: string) {
    return this.request<any>('GET', `/backlog/categories?project_key=${projectKey}`);
  }
}

export const api = new ApiClient();
